/**
 * Pricing a line without asking anybody — from a past quote, or from our own shelf.
 *
 * ---- The shape of the problem ----
 *
 * An RFQ exists to discover a price. When the price is already known there is nothing to
 * discover, and sending one wastes days. Blanket orders were the first such source; these are the
 * other two a purchaser actually uses:
 *
 *   from history   "Dolphin quoted 45 for this last month — take that"
 *   from stock     "we have twelve on the shelf, supply from our own"
 *
 * Both produce the same three writes, because both mean the same thing to the rest of the app:
 * this line skips the RFQ (`isRfqLine` excludes `contract`), carries a known unit cost, and knows
 * who it comes from. `contractQuotation.ts` then prices it straight onto the customer quotation.
 *
 * ---- Why only three fields ----
 *
 * `route`, `selected_rate` and `suppliers` are the ones `update_enquiry` actually persists —
 * measured 2026-08-29, when `contract_id` and an unknown key were both silently dropped. Writing
 * anything else would look like the choice had been recorded in full when it had not.
 */

import type { EnquiryItem, EnquiryItemPatch, PurchaseHistoryEntry } from './types'

/**
 * Stands in the `suppliers` array for a line we supply ourselves.
 *
 * A marker rather than an empty array, deliberately: `unassignedRfqLines` warns about lines with
 * no supplier, and an in-house line would otherwise be indistinguishable from one still waiting
 * for somebody to choose. It is also what the quotation shows as the source.
 *
 * NOT a `Supplier` docname — nothing writes it to a Link field. `route: 'contract'` keeps the line
 * out of `buildRfqPlan` entirely, so it never reaches RFQ generation where a real code is needed.
 */
export const IN_HOUSE_SUPPLIER = 'In-house stock'

/** The three fields a directly-priced line needs; `null` when the source cannot price it. */
type DirectPrice = Pick<EnquiryItemPatch, 'route' | 'selected_rate' | 'suppliers'>

/**
 * Take a supplier's past quote as this line's price.
 *
 * The rate is copied verbatim from what they actually quoted — no margin, no conversion, no
 * restating into another unit. It is a cost, and `contractQuotation.ts` applies the margin when
 * the customer quotation is built.
 */
export function priceFromHistory(entry: PurchaseHistoryEntry): DirectPrice | null {
  const rate = entry.price.amount
  // A zero is a DECLINED reply, not a price — the same rule `quoteBenchmark.ts` and the workbook
  // reader apply. Pricing a line at zero quotes the customer free goods.
  if (!Number.isFinite(rate) || rate <= 0) return null
  // Without a supplier there is nobody to buy from, and the quotation would name no source.
  if (entry.supplier.trim() === '') return null
  return { route: 'contract', selected_rate: rate, suppliers: [entry.supplier] }
}

/**
 * Supply the line from our own stock at a known unit cost.
 *
 * The figure is a COST — the selling price is decided on the customer quotation page, where the
 * margin rules are applied (Akhil, 2026-09-03). `contractQuotation.ts` margins an in-house line
 * exactly like a supplier-sourced one; there is no special case and must not be one.
 *
 * Worth keeping in view: the catalogue field this is usually fed from is labelled a SELLING price
 * on this bench (ten items carry a real buying price against 4,253 with only a selling one). Per
 * the ruling above we treat what we hold as the cost. If a margin ever looks doubled on in-house
 * lines, THIS is the place to look first — the fix would be to source a true buying price, not to
 * reintroduce the zero-margin special case that used to live downstream.
 */
export function priceFromStock(unitCost: number): DirectPrice | null {
  if (!Number.isFinite(unitCost) || unitCost <= 0) return null
  return { route: 'contract', selected_rate: unitCost, suppliers: [IN_HOUSE_SUPPLIER] }
}

export interface StockPricingPlan {
  actions: Array<{ lineId: string; patch: DirectPrice }>
  /** Lines with no price of ours in their unit — left as they are. */
  noPrice: number
  /** Lines in the plan that already carry another price (a contract, a past quote). */
  replacing: number
}

/**
 * "Use our price" for many lines at once (Akhil, 2026-11-03: apply the base / in-stock price to
 * all the items in the enquiry with one click). Each line gets exactly what the row's in-house
 * badge writes (`priceFromStock`), at `priceFor(line)` — the caller's per-line, per-unit figure.
 *
 * A line already on our stock at that figure is skipped, so a second click writes nothing. A line
 * priced some other way IS replaced — the purchaser asked for all — and counted in `replacing` so
 * they can be told before it happens.
 */
export function planStockPricing(
  lines: readonly EnquiryItem[],
  priceFor: (line: EnquiryItem) => number | null,
): StockPricingPlan {
  const plan: StockPricingPlan = { actions: [], noPrice: 0, replacing: 0 }
  for (const line of lines) {
    const amount = line.item_code ? priceFor(line) : null
    const patch = amount === null ? null : priceFromStock(amount)
    if (!patch) {
      plan.noPrice += 1
      continue
    }
    const ours = line.route === 'contract' && line.suppliers.length === 1 && line.suppliers[0] === IN_HOUSE_SUPPLIER
    if (ours && line.selected_rate === patch.selected_rate) continue
    if (!ours && line.route === 'contract' && Number(line.selected_rate) > 0) plan.replacing += 1
    plan.actions.push({ lineId: line.line_id, patch })
  }
  return plan
}
